import React, { useState } from "react";
import ReactTooltip from "react-tooltip";
import { toast } from "react-toastify";
import {
  MAIN_REASONS,
  SANCTION_TYPES,
  PATH_TYPES,
  PROPOSAL_TYPES,
} from "../../../RecipientDetail/sanctionConstants";
import { MODERATION_REASONS } from "../../../RecipientDetail/moderationConstants";
import { INFORMATIONS_COMPLEMENTAIRES_LABELS } from "./informationsComplementairesConstants";
import BadgeOrigineFranceTravail, {
  estOrigineFranceTravail,
} from "../../BadgeOrigineFranceTravail";
import BadgeDemandeSuspensionRemobilisation, { aDemandeSuspensionRemobilisation } from "../../EncartDemandeSuspensionRemobilisation";
import { Pill } from "../../../../common/DataList";
import dataService from "../../../../../helpers/dataService";
import tools from "../../../../../helpers/tools";
import { estAnonymise } from "../../../../../helpers/allocataire";

const EpRecipientsList = ({
  recipients,
  onDelete,
  onUpdateRecipient,
  disabled,
}) => {
  const getReasonLabel = (reasonId) => {
    const reason = Object.values(MAIN_REASONS).find((r) => r.id === reasonId);
    return reason?.label || reasonId;
  };

  const getSanctionTypeLabel = (type) => {
    return type === SANCTION_TYPES.SUSPENSION ? "Suspension" : "Suppression";
  };

  const getSanctionTypeBadge = (type) => {
    return type === SANCTION_TYPES.SUSPENSION ? "warning" : "danger";
  };

  const getSecondaryReasonLabel = (reasonId) => {
    const reason = Object.values(MODERATION_REASONS).find(
      (r) => r.id === reasonId,
    );
    return reason?.label || reasonId;
  };

  const getPathTypeLabel = (pathTypeId) => {
    const pathType = Object.values(PATH_TYPES).find((p) => p.id === pathTypeId);
    return pathType?.label || pathTypeId;
  };

  const getProposalLabel = (proposalId) => {
    const proposal = Object.values(PROPOSAL_TYPES).find(
      (p) => p.id === proposalId,
    );
    return proposal?.label || proposalId;
  };

  const getSddStatusLabel = (sddStatus) => {
    return sddStatus ? "SDD" : "NSDD";
  };

  const getDetectionTypeBadge = (detectionType) => {
    const styles = {
      PREMIER: {
        bg: "#17a2b8",
        color: "#fff",
        label: "Premier",
      },
      PERSISTANCE: {
        bg: "#ffc107",
        color: "#fff",
        label: "Persistance",
      },
      REITERATION: {
        bg: "#dc3545",
        color: "#fff",
        label: "Réitération",
      },
    };
    return styles[detectionType] || null;
  };

  const [expandedId, setExpandedId] = useState(null);

  const toggleInfoComplementaires = (recipientId) => {
    setExpandedId((prev) => (prev === recipientId ? null : recipientId));
  };

  const handleToggleMotif = (recipient, motifId) => {
    const current = recipient.additionalInformations || [];
    const updated = current.includes(motifId)
      ? current.filter((m) => m !== motifId)
      : [...current, motifId];

    onUpdateRecipient(recipient.id, { additionalInformations: updated });
    dataService.patch(
      `inscriptions-ep/${recipient.id}/modifier-inscription`,
      { informationsComplementaires: updated },
      () => {},
      (err) => {
        toast.error(
          err?.other || "Erreur lors de la mise à jour des informations",
        );
        onUpdateRecipient(recipient.id, { additionalInformations: current });
      },
    );
  };

  if (recipients.length === 0) {
    return (
      <div className="text-center p-5">
        <i className="fa fa-users fa-3x text-muted mb-3" />
        <p className="text-muted" style={{ fontSize: 16 }}>
          Aucun allocataire inscrit à cette EP pour le moment.
        </p>
      </div>
    );
  }

  return (
    <div className="list-group border-0">
      {recipients.map((recipient, index) => {
        const anonymise = estAnonymise(recipient.userRecipient);
        return (
        <div
          key={recipient.id}
          className="list-group-item mb-0 p-0 rounded-0  border-top-0 border-left-0 border-right-0 pb-3 border-bottom-0"
        >
          <div className="d-flex justify-content-end mb-1">
            {!anonymise && onDelete &&
              (() => {
                const canDelete = recipient.peutEtreSupprimee?.ok !== false;
                const tooltipId = `delete-inscription-${recipient.id}`;
                return (
                  <>
                    <button
                      id={tooltipId}
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => canDelete && onDelete(recipient.id)}
                      disabled={!canDelete}
                      data-tip={
                        !canDelete && recipient.peutEtreSupprimee?.raison
                          ? recipient.peutEtreSupprimee.raison
                          : ""
                      }
                      data-for={tooltipId}
                      style={{
                        fontSize: 12,
                        borderRadius: 6,
                        padding: "2px 10px",
                      }}
                    >
                      <i
                        className="fa fa-trash mr-1"
                        style={{ fontSize: 11 }}
                      />
                      Retirer
                    </button>
                    {!canDelete && recipient.peutEtreSupprimee?.raison && (
                      <ReactTooltip id={tooltipId} place="top" effect="solid" />
                    )}
                  </>
                );
              })()}
          </div>
          <div className="row px-3">
            {/* Colonne 1 : Informations allocataire */}
            <div className="col-md-5 px-0">
              {/* Encart informations allocataire */}
              {recipient.userRecipient && (
                <div
                  className="p-3 mb-3 border-top border-right border-bottom h-100"
                  style={{
                    background: "#f8f9fa",
                    borderLeft: "4px solid #007bff",
                    borderTopLeftRadius: 10,
                    borderBottomLeftRadius: 10,
                  }}
                >
                  <div
                    className="font-weight-bold mb-1"
                    style={{ fontSize: 15 }}
                  >
                    {recipient.userRecipient.userRecipientRef ? (
                      <a
                        href={tools.getRecipientLinkFromParams(
                          recipient.userRecipient.userRecipientRef,
                          recipient.userRecipient.id,
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#007bff", textDecoration: "none" }}
                      >
                        {recipient.userRecipient.fullname}
                        <i
                          className="fa fa-external-link-alt ml-2"
                          style={{ fontSize: 12 }}
                        />
                      </a>
                    ) : (
                      recipient.userRecipient.fullname
                    )}
                    {anonymise && (
                      <Pill tone="muted" icon="fa-user-secret" className="ml-2">
                        Anonymisé
                      </Pill>
                    )}
                  </div>

                  <div className="text-dark mb-1" style={{ fontSize: 12 }}>
                    <span>
                      {getSddStatusLabel(recipient.userRecipient.sddStatus)}
                    </span>{" "}
                    • <span>{recipient.userRecipient.age}</span> •{" "}
                    <span>{recipient.userRecipient.maritalStatusLabel}</span> •{" "}
                    <span>
                      {recipient.userRecipient.nbChildrenOrDependant} personnes
                      à charge
                    </span>
                    •{" "}
                    <span>{recipient.userRecipient.professionalSituation}</span>
                  </div>

                  <div className="d-flex flex-wrap" style={{ gap: 5 }}>
                    {estOrigineFranceTravail(recipient) && (
                      <div className="mb-1">
                        <BadgeOrigineFranceTravail />
                      </div>
                    )}
                    {aDemandeSuspensionRemobilisation(recipient) && (
                      <div className="mb-1">
                        <BadgeDemandeSuspensionRemobilisation
                          demandeSuspensionRemobilisation={recipient.demandeSuspensionRemobilisation}
                          allocataireNom={recipient.userRecipient?.fullname}
                        />
                      </div>
                    )}
                    {recipient.pregnancy && (
                      <div className="mb-1">
                        <span
                          className="badge bg-white border-danger border text-danger"
                          style={{ fontSize: 11 }}
                        >
                          <i className="fa fa-baby mr-1" />
                          Grossesse déclarée
                        </span>
                      </div>
                    )}
                    {recipient.userRecipient.age &&
                      parseInt(recipient.userRecipient.age) > 62 && (
                        <div className="mb-1">
                          <span
                            className="badge bg-white border-danger border text-danger"
                            style={{ fontSize: 11 }}
                          >
                            <i className="fa fa-exclamation-triangle mr-1" />
                            Plus de 62 ans
                          </span>
                        </div>
                      )}
                  </div>

                  <div
                    className="d-flex flex-column mt-1"
                    style={{ fontSize: 12 }}
                  >
                    {recipient.userRecipient.sddStatus !== undefined &&
                      recipient.userRecipient.rsaTypeLabel && (
                        <span className=" text-muted " style={{ fontSize: 12 }}>
                          Type RSA :{" "}
                          <span className="text-dark">
                            {recipient.userRecipient.rsaTypeLabel}
                          </span>
                        </span>
                      )}
                    <span className="text-muted">
                      NIR :{" "}
                      <span className="text-dark">
                        {recipient.userRecipient.nir}
                      </span>
                    </span>
                    {recipient.userRecipient.orientationPartner && (
                      <span className="text-muted">
                        Orientation :{" "}
                        <span className="text-dark">
                          {recipient.userRecipient.orientationPartner}
                        </span>
                      </span>
                    )}

                    {recipient.userRecipient.city &&
                      recipient.userRecipient.postalCode && (
                        <span className="text-muted">
                          Localisation:{" "}
                          <span className="text-dark">
                            {recipient.userRecipient.postalCode}{" "}
                            {recipient.userRecipient.city}
                          </span>
                        </span>
                      )}
                  </div>
                </div>
              )}
            </div>

            {/* Colonne 2 : Informations de l'inscription */}
            <div className="col-md-7 px-0">
              <div
                className="p-3  border-top  border-bottom h-100"
                style={{
                  background: "#f8f9fa",
                  borderRight: `4px solid ${
                    recipient.type === "SUSPENSION" ? "#ffc107" : "#dc3545"
                  }`,
                  borderTopRightRadius: 10,
                  borderBottomRightRadius: 10,
                }}
              >
                <div className="mb-2">
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    Sanction proposée
                  </span>
                  <div className="mb-2 mt-1" style={{ fontSize: 12 }}>
                    <span
                      className={`badge badge-${getSanctionTypeBadge(recipient.type)}`}
                      style={{ fontSize: 12, fontWeight: 600 }}
                    >
                      {getSanctionTypeLabel(recipient.type)}
                    </span>
                    <span className="font-weight-bold">
                      {recipient.proposalPercent &&
                        recipient.proposalDuration && (
                          <span>
                            {" "}
                            de {recipient.proposalPercent}% pendant{" "}
                            {recipient.proposalDuration} mois
                          </span>
                        )}
                    </span>
                    {recipient.selectedProposal && (
                      <span
                        className="text-muted ml-2"
                        style={{ fontSize: 12 }}
                      >
                        (
                        {getProposalLabel(
                          recipient.selectedProposal,
                        ).toLowerCase()}
                        )
                      </span>
                    )}
                  </div>
                </div>

                <div className="mb-2">
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    Motif principal
                  </span>
                  <div
                    className="font-weight-bold mt-1"
                    style={{ fontSize: 12 }}
                  >
                    {recipient.detectionType && (
                      <span
                        className="badge mr-2"
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          backgroundColor: getDetectionTypeBadge(
                            recipient.detectionType,
                          )?.bg,
                          color: getDetectionTypeBadge(recipient.detectionType)
                            ?.color,
                        }}
                      >
                        {getDetectionTypeBadge(recipient.detectionType)?.label}
                      </span>
                    )}
                    {getReasonLabel(recipient.motif)}
                  </div>
                  {recipient.pathType &&
                    recipient.motif === "REFUS_OFFRE_RAISONNABLE_EMPLOI" && (
                      <div className="mt-1">
                        <small className="" style={{ fontSize: 11 }}>
                          Parcours : {getPathTypeLabel(recipient.pathType)}
                        </small>
                      </div>
                    )}
                </div>

                {recipient.secondaryReasons &&
                  recipient.secondaryReasons.length > 0 && (
                    <div className="mb-2">
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        Motifs secondaires
                      </span>
                      <div className="d-flex flex-wrap mt-1" style={{ gap: 5 }}>
                        {recipient.secondaryReasons.map((motif, idx) => (
                          <span
                            key={idx}
                            className="badge badge-light"
                            style={{ fontSize: 11 }}
                          >
                            {getSecondaryReasonLabel(motif)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Informations complémentaires */}
                <div
                  className="mt-2"
                  style={{ borderTop: "1px solid #e9ecef", paddingTop: 8 }}
                >
                  <button
                    className="btn btn-sm btn-secondary d-flex align-items-center"
                    onClick={() => toggleInfoComplementaires(recipient.id)}
                    style={{
                      fontSize: 12,
                      borderRadius: 6,
                      padding: "3px 10px",
                      gap: 6,
                    }}
                  >
                    <i
                      className={`fa ${expandedId === recipient.id ? "fa-chevron-up" : "fa-chevron-down"}`}
                      style={{ fontSize: 10 }}
                    />
                    Informations complémentaires
                    {recipient.additionalInformations?.length > 0 && (
                      <span
                        className="badge badge-info ml-1"
                        style={{ fontSize: 10 }}
                      >
                        {recipient.additionalInformations.length}
                      </span>
                    )}
                  </button>

                  {expandedId === recipient.id && (
                    <div
                      className="mt-2 p-2 rounded"
                      style={{
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <div className="d-flex flex-column" style={{ gap: 4 }}>
                        {Object.entries(
                          INFORMATIONS_COMPLEMENTAIRES_LABELS,
                        ).map(([motifId, label]) => {
                          const isChecked = (
                            recipient.additionalInformations || []
                          ).includes(motifId);
                          return (
                            <label
                              key={motifId}
                              className="d-flex align-items-center mb-0"
                              style={{
                                fontSize: 12,
                                cursor: "pointer",
                                padding: "3px 6px",
                                borderRadius: 4,
                                background: isChecked
                                  ? "#eff6ff"
                                  : "transparent",
                                gap: 8,
                              }}
                            >
                              <input
                                disabled={disabled || anonymise}
                                type="checkbox"
                                checked={isChecked}
                                onChange={() =>
                                  handleToggleMotif(recipient, motifId)
                                }
                              />
                              <span>{label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
};

export default EpRecipientsList;
